import { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import BaseModal from '@/component/modal/BaseModal';
import SolidButton from '@/component/button/SolidButton';
import BorderButton from '@/component/button/BorderButton';
import { toast } from 'react-toastify';
import {
  useStartMindHealthSession,
  useCancelMindHealthSession,
  useGetMindHealthSession,
  useGetMindHealthSessions,
  apiGetSessionBCGData,
  SessionStatus
} from '@/api/mentalHealth/mindHealthSession';
import { useMindHealthSessionStore } from '@/store/MindHealthSessionStore';
import { analyzeBCGData } from '@/util/bcg';
import { AnalysisParams } from '@/util/bcg/types';
import { mentalHealthStore } from '@/store/MentalHealthStore';

interface BCGSessionModalProps {
  userId?: number;
  userName?: string;
  onSuccess?: (results: any) => void;
}

export interface BCGSessionModalRef {
  show: () => void;
  close: () => void;
}

const BCGSessionModal = forwardRef<BCGSessionModalRef, BCGSessionModalProps>(({
  userId,
  userName,
  onSuccess,
}, ref) => {
  const { t } = useTranslation();
  const modalRef = useRef<any>(null);

  // Store
  const {
    currentSession,
    isSessionActive,
    setCurrentSession,
    clearSession,
    getProgress,
    isSessionCompleted,
  } = useMindHealthSessionStore();

  // 분석 파라미터 (기본값)
  const [params] = useState<AnalysisParams>({
    windowSec: 60,
    strideSec: 15,
    mode: 'absolute',
    calibrationPairs: [],
  });

  // API Mutations
  const startSessionMutation = useStartMindHealthSession();
  const cancelSessionMutation = useCancelMindHealthSession();

  // 진행 중인 세션 확인 (모달이 열릴 때)
  const { data: activeSessionsData } = useGetMindHealthSessions(
    userId || 0,
    { status: 'IN_PROGRESS', pageSize: 1 },
    !!userId && !currentSession // userId가 있고 현재 세션이 없을 때만 조회
  );

  // 진행 중인 세션이 있으면 자동으로 복구
  useEffect(() => {
    if (activeSessionsData?.data && activeSessionsData.data.length > 0) {
      const activeSession = activeSessionsData.data[0];
      setCurrentSession(activeSession);
    }
  }, [activeSessionsData]);

  // 세션 상태 폴링 (INITIALIZING 또는 IN_PROGRESS일 때만)
  const { data: sessionData } = useGetMindHealthSession(
    userId || 0,
    currentSession?.sessionId || '',
    !!currentSession && isSessionActive,
    2000 // 2초마다 폴링
  );

  // 세션 데이터가 업데이트되면 Store 업데이트
  useEffect(() => {
    if (sessionData) {
      setCurrentSession(sessionData);

      // 완료 상태가 되면 BCG 데이터 가져오기
      if (sessionData.status === 'COMPLETED') {
        handleSessionCompleted();
      }

      // 에러 상태 처리
      if (isErrorStatus(sessionData.status)) {
        handleSessionError(sessionData.status, sessionData.notes);
      }
    }
  }, [sessionData]);

  useImperativeHandle(ref, () => ({
    show: () => modalRef.current?.show(),
    close: () => modalRef.current?.close(),
  }));

  // 세션 시작
  const handleStartSession = async () => {
    if (!userId) {
      toast.error(t('mentalHealth.session.error.noUser'));
      return;
    }

    try {
      const result = await startSessionMutation.mutateAsync(userId);
      setCurrentSession(result.data || null);
      toast.success(t('mentalHealth.session.started'));
    } catch (error: any) {
      // console.error('세션 시작 실패:', error);

      // 에러 메시지 처리
      const errorMessage = error?.response?.data?.message || error?.message;
      if (error?.response?.status === 409) {
        toast.error(t('mentalHealth.session.error.alreadyInProgress'));
      } else if (error?.response?.status === 503) {
        toast.error(t('mentalHealth.session.error.deviceOffline'));
      } else {
        toast.error(t('mentalHealth.session.error.startFailed', { message: errorMessage }));
      }
    }
  };

  // 세션 취소
  const handleCancelSession = async () => {
    if (!userId || !currentSession) return;

    try {
      await cancelSessionMutation.mutateAsync({
        protecteeId: userId,
        sessionId: currentSession.sessionId,
      });
      toast.info(t('mentalHealth.session.cancelled'));
      clearSession();
    } catch (error) {
      // console.error('세션 취소 실패:', error);
      toast.error(t('mentalHealth.session.error.cancelFailed'));
    }
  };

  // 세션 완료 처리
  const handleSessionCompleted = async () => {
    if (!userId || !currentSession) return;

    try {
      // BCG 데이터 가져오기
      const bcgDataResponse = await apiGetSessionBCGData(userId, currentSession.sessionId);

      if (!bcgDataResponse.data) {
        toast.error(t('mentalHealth.session.error.noData'));
        return;
      }

      const bcgData = bcgDataResponse.data.bcgData;

      if (!bcgData || bcgData.length === 0) {
        toast.error(t('mentalHealth.session.error.noData'));
        return;
      }

      // BCG 데이터를 CSV 형식으로 변환 (기존 분석 함수 호환)
      const csvData = convertBCGDataToCSVFormat(bcgData);

      // 분석 수행
      const report = analyzeBCGData(csvData, params);

      if (report) {
        // 분석 결과 저장 (sessionId를 ID로 사용)
        const storedReport = mentalHealthStore.saveReport(
          `Session_${currentSession.sessionId}`,
          report,
          userId,
          userName,
          currentSession.sessionId // customId로 sessionId 전달
        );

        toast.success(t('mentalHealth.session.analysisComplete'));

        // 성공 콜백 호출
        onSuccess?.({ reports: [{ filename: storedReport.filename, report }] });

        // 모달 닫기
        handleCloseModal();
      } else {
        toast.error(t('mentalHealth.session.error.analysisFailed'));
      }
    } catch (error) {
      toast.error(t('mentalHealth.session.error.analysisFailed'));
    }
  };

  // BCG 데이터를 CSV 형식으로 변환
  const convertBCGDataToCSVFormat = (bcgData: number[], chunkSize: number = 256) => {
    // API에서 받은 BCG 데이터를 기존 CSV 파싱 결과와 동일한 형식으로 변환
    // bcgData는 모든 청크의 데이터가 합쳐진 배열이므로, 이를 청크 단위로 나눠야 함
    const csvRows = [];
    const totalChunks = Math.ceil(bcgData.length / chunkSize);

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, bcgData.length);
      const chunkData = bcgData.slice(start, end);

      // 각 청크를 CSV 행으로 변환
      csvRows.push({
        deviceTime: new Date(Date.now() + i * 1000).toISOString(), // 1초 간격으로 시간 설정
        bcgMerge: JSON.stringify({ data: chunkData }),
      });
    }

    return csvRows;
  };

  // 에러 상태 확인
  const isErrorStatus = (status: SessionStatus) => {
    return ['FAILED', 'ABORTED_TIMEOUT', 'ABORTED_OFFLINE', 'ABORTED_UNEXPECTED'].includes(status);
  };

  // 에러 처리
  const handleSessionError = (status: SessionStatus, notes: string | null) => {
    let errorMessage = '';

    switch (status) {
      case 'ABORTED_TIMEOUT':
        errorMessage = t('mentalHealth.session.error.timeout');
        break;
      case 'ABORTED_OFFLINE':
        errorMessage = t('mentalHealth.session.error.deviceOffline');
        break;
      case 'ABORTED_UNEXPECTED':
        errorMessage = t('mentalHealth.session.error.serverError');
        break;
      case 'FAILED':
        errorMessage = notes || t('mentalHealth.session.error.failed');
        break;
    }

    toast.error(errorMessage);
    clearSession();
  };

  // 모달 닫기 요청 (버튼 클릭 등)
  const handleCloseModal = () => {
    modalRef.current?.close();
  };

  // 모달이 실제로 닫힐 때 호출되는 콜백 (BaseModal의 onCloseCallback)
  const handleModalClosed = () => {
    clearSession();
  };

  // 상태별 메시지
  const getStatusMessage = () => {
    if (!currentSession) return '';

    switch (currentSession.status) {
      case 'INITIALIZING':
        return t('mentalHealth.session.status.initializing');
      case 'IN_PROGRESS':
        return t('mentalHealth.session.status.inProgress');
      case 'COMPLETED':
        return t('mentalHealth.session.status.completed');
      case 'CANCELLED':
        return t('mentalHealth.session.status.cancelled');
      default:
        return '';
    }
  };

  // 상태별 아이콘
  const getStatusIcon = () => {
    if (!currentSession) return '🔄';

    switch (currentSession.status) {
      case 'INITIALIZING':
        return '🔄';
      case 'IN_PROGRESS':
        return '📊';
      case 'COMPLETED':
        return '✅';
      case 'CANCELLED':
        return '❌';
      default:
        return '⚠️';
    }
  };

  return (
    <BaseModal ref={modalRef} onCloseCallback={handleModalClosed} width="600" showCloseButton={false}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold">
              {t('mentalHealth.session.title')}
            </h2>
            {userName && (
              <p className="text-sm text-gray-600 mt-1">
                {t('mentalHealth.modal.targetMember')}: <strong>{userName}</strong>
              </p>
            )}
          </div>
          <button
            onClick={handleCloseModal}
            className="text-gray-400 hover:text-gray-600 text-2xl"
            disabled={isSessionActive}
          >
            ×
          </button>
        </div>

        {!currentSession ? (
          // 세션 시작 전
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-2">
                {t('mentalHealth.session.info.title')}
              </h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• {t('mentalHealth.session.info.duration')}</li>
                <li>• {t('mentalHealth.session.info.device')}</li>
                <li>• {t('mentalHealth.session.info.cancel')}</li>
              </ul>
            </div>

            <div className="flex justify-end space-x-3">
              <BorderButton
                text={t('mentalHealth.modal.button.cancel')}
                onClick={handleCloseModal}
              />
              <SolidButton
                text={t('mentalHealth.session.button.start')}
                onClick={handleStartSession}
                disabled={startSessionMutation.isPending}
              />
            </div>
          </div>
        ) : (
          // 세션 진행 중
          <div className="space-y-6">
            {/* 상태 표시 */}
            <div className="text-center py-8">
              <div className="text-6xl mb-4 animate-pulse">{getStatusIcon()}</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {getStatusMessage()}
              </h3>

              {currentSession.status === 'IN_PROGRESS' && (
                <>
                  <div className="mt-6 mb-2">
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-emerald-600 h-4 rounded-full transition-all duration-500"
                        style={{ width: `${getProgress()}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('mentalHealth.session.progress', {
                      received: currentSession.receivedChunkCount,
                      total: currentSession.expectedTotalChunks,
                      percent: getProgress()
                    })}
                  </p>
                </>
              )}
            </div>

            {/* 세션 정보 */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('mentalHealth.session.sessionId')}:</span>
                <span className="font-mono text-xs">{currentSession.sessionId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('mentalHealth.session.startTime')}:</span>
                <span>{new Date(currentSession.startTime).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('mentalHealth.session.deviceId')}:</span>
                <span>{currentSession.deviceId}</span>
              </div>
            </div>

            {/* 버튼 */}
            <div className="flex justify-end space-x-3">
              {isSessionActive && (
                <button
                  onClick={handleCancelSession}
                  disabled={cancelSessionMutation.isPending}
                  className="px-6 py-3 border-2 border-red-500 text-red-500 hover:bg-red-50 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('mentalHealth.session.button.cancel')}
                </button>
              )}
              {isSessionCompleted() && (
                <SolidButton
                  text={t('mentalHealth.modal.button.done')}
                  onClick={handleCloseModal}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
});

export default BCGSessionModal;

